import { ClassValue } from 'clsx';
import { isNil } from 'lodash';
import { Text } from '@shared/components/Text';
import { ButtonView, View } from '@shared/components/View';
import { Pill } from '@shared/components/pill';
import { getEntityTitle, isDeletedEntity } from '@shared/data/entity';
import { RelatedEntityField, RelatedItem } from '@shared/data/entity/fields/RelatedEntityField';
import { useOpenRelatedEntity } from '@shared/scenes/entity/components/fields/RelatedEntityEditField/useOpenRelatedEntity';
import { getComponentLabel } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { notNil } from '@shared/util/notNil';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { FieldVariant } from './FieldVariant';
import { getRelatedCardText, getRelatedCardTitle } from './util/getRelatedCardText';
import styles from './RelatedCardCollectionViewField.module.css';

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  field: RelatedEntityField;
  component: API.AnyComponent;
}

const MAX_CARDS_TO_SHOW = 3;

/**
 * Default: a pill per card, up to three, and a "+N". Compact: the first card as a pill
 * that opens it, and a "+N"; text when the rows' refs don't hold it. Inline: the first card's
 * title and a "+N".
 */
export function RelatedCardCollectionViewField({ field, component, variant, className }: Props) {
  if (field.isEmpty) {
    return null;
  }

  const isInline = variant === 'inline';
  const isCompact = variant === 'compact';
  const isDefault = variant === 'default';

  return (
    <CollectionViewFieldContainer
      fieldType={field.fieldType}
      variant={variant}
      className={[styles.relatedCardCollectionViewField, className]}
    >
      {isInline && <InlineRelatedCard field={field} />}
      {isCompact && <CompactRelatedCard field={field} />}
      {isDefault && <DefaultRelatedCard field={field} component={component} />}
    </CollectionViewFieldContainer>
  );
}

function InlineRelatedCard({ field }: { field: RelatedEntityField }) {
  return <>{getRelatedCardText(field)}</>;
}

function DefaultRelatedCard({
  field,
  component,
}: {
  field: RelatedEntityField;
  component: API.AnyComponent;
}) {
  const validItems = field.items.filter(item =>
    field.relatedEntities.some(relatedItem => relatedItem.id === item.id),
  );
  const itemsToShow = validItems.slice(0, MAX_CARDS_TO_SHOW);
  const remainingCardCount = validItems.length - MAX_CARDS_TO_SHOW;
  const defaultEntityName = i18n('workflowTemplate.defaultRelatedCard', {
    workflowName: getComponentLabel(component),
  });

  return (
    <>
      {itemsToShow.map(item => (
        <RelatedCardPill
          key={item.id}
          item={item}
          field={field}
          defaultEntityName={defaultEntityName}
        />
      ))}
      {remainingCardCount > 0 && <Pill sections={[`+${remainingCardCount}`]} noBorder />}
    </>
  );
}

/** The first card the refs hold, as a pill that opens it (italic once deleted), and a "+N". */
function CompactRelatedCard({ field }: { field: RelatedEntityField }) {
  const openRelatedEntity = useOpenRelatedEntity();
  const items = field.items.filter(item =>
    field.relatedEntities.some(entity => entity.id === item.id),
  );
  const [firstItem] = items;
  const entity = field.relatedEntities.find(relatedEntity => relatedEntity.id === firstItem?.id);
  if (isNil(firstItem) || isNil(entity)) {
    return <Text numberOfLines={1}>{getRelatedCardText(field)}</Text>;
  }
  const isDeleted = isDeletedEntity(entity);
  const title = getRelatedCardTitle(field, entity);
  const quantity = firstItem.quantity?.toString();
  const moreCount = items.length - 1;
  return (
    <View row centerVertically className={styles.line}>
      <ButtonView
        disabled={isDeleted}
        onClick={() => void openRelatedEntity(entity)}
        className={styles.button}
      >
        <Pill sections={[quantity, title].filter(notNil)} italic={isDeleted} />
      </ButtonView>
      {moreCount > 0 && <Text weight="normal">{`+${moreCount}`}</Text>}
    </View>
  );
}

interface RelatedCardPillProps {
  item: RelatedItem;
  field: RelatedEntityField;
  defaultEntityName: string;
}

function RelatedCardPill({ item, field, defaultEntityName }: RelatedCardPillProps) {
  const entity = field.relatedEntities.find(e => e.id === item.id);
  const workflowTemplate = field.workflowTemplateRefs?.find(
    template => template.id === entity?.workflowTemplateId,
  );
  const isDeleted = isDeletedEntity(entity);
  const quantity = item.quantity?.toString();
  const entityName = getEntityTitle(entity, workflowTemplate) ?? defaultEntityName;
  return (
    <Pill sections={[quantity, entityName].filter(notNil)} numberOfLines={2} italic={isDeleted} />
  );
}
